/**
 * MixAgent.js
 * "The Virtual Session Musician"
 * Handles semantic gain staging and context-aware mixing.
 */

export const MixRoles = {
    ANCHOR: 'anchor',       // Kick, Sub (The Foundation)
    LEAD: 'lead',           // Melody, Solo, Vocals (The Focus)
    SUPPORT: 'support',     // Chords, Rhythm, Bass (The Body)
    ATMOSPHERE: 'atmosphere'// Pads, Texture (The Glue)
};

const MIX_TARGETS = {
    [MixRoles.ANCHOR]: -6,
    [MixRoles.LEAD]: -9,
    [MixRoles.SUPPORT]: -12,
    [MixRoles.ATMOSPHERE]: -18
};

export class MixAgent {
    constructor() {
        this.registry = {}; // { id: { role, strip } }
        this.currentLoadState = 'normal'; // normal, high, critical
    }

    /**
     * Infers the MixRole from an instrument definition.
     * @param {Object} instrument - Instrument definition from Library.
     * @returns {string} MixRole
     */
    getRole(instrument) {
        if (!instrument) return MixRoles.SUPPORT;

        const roles = instrument.roles || [];
        const tags = instrument.tags || [];
        const id = instrument.id || '';

        // Anchor
        if (id.includes('kick') || id.includes('sub') || roles.includes('bass_drum')) return MixRoles.ANCHOR;
        // Exception: Bass can be Anchor or Support. Let's make Sub-Bass Anchor, others Support.
        if (id === 'bass_drop') return MixRoles.ANCHOR;

        // Lead
        if (roles.includes('melody') || roles.includes('solo') || roles.includes('lead')) return MixRoles.LEAD;

        // Atmosphere
        if (roles.includes('pad') || roles.includes('atmosphere') || tags.includes('ambient')) return MixRoles.ATMOSPHERE;

        // Default Support
        return MixRoles.SUPPORT;
    }

    /**
     * Registers an instrument strip for management.
     * Applies baseline gain immediately.
     * @param {string} id - Unique instance ID.
     * @param {string} role - MixRole.
     * @param {Object} strip - ChannelStrip instance.
     */
    register(id, role, strip) {
        if (!strip) return;

        this.registry[id] = { role, strip };

        // Apply Baseline immediately
        const target = MIX_TARGETS[role];
        // Ramp to target
        if (strip.volume && strip.volume.rampTo) {
             strip.volume.rampTo(target, 0.1);
        }
    }

    unregister(id) {
        delete this.registry[id];
    }

    /**
     * Optimizes the mix based on system load.
     * Mutes low-priority layers to preserve audio stability.
     * @param {number} cpuLoad - 0.0 to 1.0 (approximate density/lag metric)
     */
    optimize(cpuLoad) {
        let newState = 'normal';
        if (cpuLoad > 0.9) newState = 'critical';
        else if (cpuLoad > 0.75) newState = 'high';

        // Only update if state changes to avoid thrashing
        if (newState !== this.currentLoadState) {
            this.currentLoadState = newState;
            console.log(`[MixAgent] Load State changed to: ${newState}`);
            // Trigger an immediate mix update to apply mutes
            this.updateMix(this.lastTension || 0.5);
        }
    }

    /**
     * Updates fader positions based on Context Tension.
     * @param {number} tension - 0.0 to 1.0
     */
    updateMix(tension) {
        this.lastTension = tension; // Store for re-runs

        Object.values(this.registry).forEach(entry => {
            const { role, strip } = entry;
            let target = MIX_TARGETS[role];

            // 1. Dynamic Adjustments based on Musical Tension
            if (tension < 0.3) {
                // Low Tension
                if (role === MixRoles.ATMOSPHERE) target += 2; // Boost Glue
                if (role === MixRoles.ANCHOR) target -= 2;     // Soften Kick
            } else if (tension > 0.7) {
                // High Tension
                if (role === MixRoles.LEAD) target += 2;       // Push Focus
                if (role === MixRoles.ANCHOR) target += 2;     // Push Power
                if (role === MixRoles.ATMOSPHERE) target -= 4; // Clear Mud
            }

            // 2. Load-Based Throttling (The "Lifeboat" Protocol)
            if (this.currentLoadState === 'critical') {
                // In Critical state, mute Atmosphere AND Support. Keep Anchor and Lead.
                if (role === MixRoles.ATMOSPHERE || role === MixRoles.SUPPORT) {
                    target = -100;
                }
            } else if (this.currentLoadState === 'high') {
                // In High state, mute Atmosphere only.
                if (role === MixRoles.ATMOSPHERE) {
                    target = -100;
                }
            }

            // Apply
            if (strip.volume && strip.volume.rampTo) {
                // If muting (-100), do it fast (0.1s). Unmuting/Mixing is smooth (1.0s).
                const time = (target === -100) ? 0.1 : 1.0;
                strip.volume.rampTo(target, time);
            }
        });
    }
}
